import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './authStore';
import { AppShell } from './AppShell';
import { RequireAuth, RequirePermission } from './guards';
import { ToastViewport } from '../components/Toast';
import { LoginPage } from '../features/auth/LoginPage';
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage';
import { FleetLayout } from '../features/fleet/FleetLayout';
import { OverviewPage } from '../features/fleet/OverviewPage';
import { DroneRegistryPage } from '../features/fleet/DroneRegistryPage';
import { TrackingPage } from '../features/tracking/TrackingPage';
import { UsersPage } from '../features/users/UsersPage';
import { AuditPage } from '../features/audit/AuditPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { NotFoundPage } from './NotFoundPage';

/**
 * 首次登录（或被管理员重置口令后）必须改密。
 * 这里用整页重定向挡住，避免用户带着临时口令继续操作。
 */
function PasswordChangeGate({ children }: { children: React.ReactNode }) {
  const mustChange = useAuthStore((state) => state.mustChangePassword);
  const location = useLocation();

  if (mustChange && location.pathname !== '/account/password') {
    return <Navigate to="/account/password" replace state={{ forced: true }} />;
  }
  return <>{children}</>;
}

export function App() {
  const bootstrap = useAuthStore((state) => state.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          element={
            <RequireAuth>
              <PasswordChangeGate>
                <AppShell />
              </PasswordChangeGate>
            </RequireAuth>
          }
        >
          <Route path="/account/password" element={<ChangePasswordPage />} />

          <Route
            path="/fleet"
            element={
              <RequirePermission permissions={['fleet.view']}>
                <FleetLayout />
              </RequirePermission>
            }
          >
            <Route index element={<OverviewPage />} />
            <Route
              path="drones"
              element={
                <RequirePermission permissions={['fleet.registry.view']}>
                  <DroneRegistryPage />
                </RequirePermission>
              }
            />
            <Route
              path="tracking/:droneId?"
              element={
                <RequirePermission permissions={['fleet.telemetry.view']}>
                  <TrackingPage />
                </RequirePermission>
              }
            />
          </Route>

          <Route
            path="/users"
            element={
              <RequirePermission permissions={['user.view']}>
                <UsersPage />
              </RequirePermission>
            }
          />
          <Route
            path="/audit"
            element={
              <RequirePermission permissions={['audit.view']}>
                <AuditPage />
              </RequirePermission>
            }
          />
          <Route
            path="/settings"
            element={
              <RequirePermission permissions={['settings.manage']}>
                <SettingsPage />
              </RequirePermission>
            }
          />

          <Route path="/" element={<Navigate to="/fleet" replace />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <ToastViewport />
    </>
  );
}
